// eslint-disable-next-line
import React, { Component } from 'react';
import { connect } from 'react-redux';

import * as MuiCore from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import AccountSelect from '../account/AccountSelect';
import * as accountSelectors from '../account/selectors';
import * as coreSelectors from '../core/selectors';
import * as serviceActions from './actions';
import GreetingCard from './GreetingCard';
import KnowledgeBaseCard from './KnowledgeBaseCard';
import FaqCard from './FaqCard';
import BotDelayCard from './BotDelayCard';
import PhoneNumbersCard from './PhoneNumbersCard';
import FormFieldsCard from './FormFieldsCard';
import FormFieldsFormCard from './FormFieldsFormCard';
import AIActionsCard from './AIActionsCard';

const styles = theme => ({
    container: {
        padding: theme.spacing(3),
        minHeight: '100vh',
        fontFamily: 'Sofia'
    },
    section: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(3),
        backgroundColor: '#fff',
        borderRadius: 20,
        fontFamily: 'Sofia',
        border: '1px solid #cccdd2'
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        borderBottom: '1px solid #e0e0e0'
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.brandingColors.rubyDarkPurple,
        fontFamily: 'Sofia-bold',
        fontSize: '1.1rem',
        '& svg': {
            color: theme.brandingColors.rubyIconColor
        }
    },
    editButton: {
        color: theme.brandingColors.rubyDarkPurple,
        borderColor: theme.brandingColors.rubyDarkPurple,
        fontFamily: 'Sofia',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 100,
        '&:hover': {
            backgroundColor: 'rgba(127, 32, 98, 0.08)',
            borderColor: theme.brandingColors.rubyMediumPurple
        }
    },
    textField: {
        width: '100%',
        marginTop: theme.spacing(2),
        fontFamily: 'Sofia',
        '& .MuiOutlinedInput-root': {
            fontFamily: 'Sofia',
            '&.Mui-focused fieldset': {
                borderColor: theme.brandingColors.rubyDarkPurple
            }
        },
        '& .MuiInputLabel-root': {
            fontFamily: 'Sofia'
        }
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
        justifyContent: 'flex-end'
    },
    tabPaper: {
        backgroundColor: '#fff',
        borderRadius: 20,
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        border: '1px solid #cccdd2',
        overflow: 'hidden'
    },
    tabs: {
        '& .MuiTab-root': {
            fontFamily: 'Sofia',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            minHeight: 64
        },
        '& .MuiTabs-indicator': {
            backgroundColor: theme.brandingColors.rubyDarkPurple,
            height: 3
        }
    },
    accountCard: {
        backgroundColor: '#fff',
        borderRadius: 20,
        padding: theme.spacing(4),
        marginBottom: theme.spacing(3),
        border: '1px solid #cccdd2',
        '& .MuiGrid-item': {
            justifyContent: 'flex-start !important',
            maxWidth: '100% !important'
        },
        '& > div > div': {
            maxWidth: '100% !important'
        },
        '& .MuiTypography-root': {
            textAlign: 'left !important',
            fontFamily: 'Sofia-bold !important',
            fontSize: '1.5rem !important',
            color: '#333 !important',
            marginBottom: theme.spacing(2)
        },
        '& .MuiFormControl-root': {
            maxWidth: '100% !important',
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
                    borderRadius: 0,
                    left: 0,
                    right: 0,
                    width: '100%'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
                    borderRadius: 0
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                    borderBottom: '2px solid #7F2062',
                    borderRadius: 0
                }
            },
            '& .MuiSelect-select': {
                padding: '12px 0',
                paddingRight: '32px',
                fontFamily: 'Sofia',
                fontSize: '1.1rem',
                color: '#888'
            }
        }
    }
});

class CallHandling extends Component {
    constructor(props) {
        super(props);
        this.botDelayCardRef = React.createRef();
    }

    state = {
        // Edit states
        editingGreeting: false,
        editingKb: false,
        editingFaq: false,
        
        // Data values (these will come from account object)
        greeting: '',
        knowledgeBase: '',
        faqItems: [],  
        botDelay: 0,  
        
        // Temporary values while editing
        tempGreeting: '',
        tempKnowledgeBase: '',
        tempFaqItems: [], 
        activeTab: 0,
        
        // Validation errors
        greetingError: '',
        knowledgeBaseError: '',
        faqErrors: {}
    };

    componentDidMount() {
        document.title = 'Ruby - Call Handling';
        this.loadDataFromAccount();
        // Load call handling data from backend
        if (this.props.account?.accountId) {
            this.props.loadCallHandling(this.props.account.accountId);
            this.props.loadPhoneNumbers(this.props.account.accountId);
            this.props.loadCallForm(this.props.account.accountId);
            this.props.loadAIActions(this.props.account.accountId);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.account?.accountId !== this.props.account?.accountId) {
            this.loadDataFromAccount();
            if (this.props.account?.accountId) {
                this.props.loadCallHandling(this.props.account.accountId);
                this.props.loadPhoneNumbers(this.props.account.accountId);
                this.props.loadCallForm(this.props.account.accountId);
                this.props.loadAIActions(this.props.account.accountId);
            }
        }
        
        // Update state when callHandlingData from backend arrives
        if (prevProps.callHandlingData !== this.props.callHandlingData && this.props.callHandlingData) {
            const faqItems = this.parseFaqToQA(this.props.callHandlingData.faq);
            this.setState({
                greeting: this.props.callHandlingData.greeting || '',
                knowledgeBase: this.props.callHandlingData.knowledgeBase || '',
                faqItems: faqItems,
                botDelay: this.props.callHandlingData.botDelay || 0,
                tempGreeting: this.props.callHandlingData.greeting || '',
                tempKnowledgeBase: this.props.callHandlingData.knowledgeBase || '',
                tempFaqItems: faqItems
            });
        }
    }

    loadDataFromAccount = () => {
        const { account } = this.props;
        if (account) {
            const faqItems = this.parseFaqToQA(account.faq);
            this.setState({
                greeting: account.greeting || '',
                knowledgeBase: account.conavKb || '',
                faqItems: faqItems,
                tempGreeting: account.greeting || '',
                tempKnowledgeBase: account.conavKb || '',
                tempFaqItems: faqItems
            });
        }
    };

    /**
     * Parse FAQ JSON from backend to Q&A array
     */
    parseFaqToQA = (faqJson) => {
        if (!faqJson) return [];
        try {
            const parsed = JSON.parse(faqJson);
            if (parsed.questions && Array.isArray(parsed.questions)) {
                return parsed.questions.map(q => {
                    let answerText = '';
                    try {
                        // Try to extract text from Draft.js format
                        const answerJson = JSON.parse(q.answer);
                        if (answerJson.blocks && Array.isArray(answerJson.blocks)) {
                            answerText = answerJson.blocks.map(b => b.text).join('\n');
                        } else {
                            answerText = q.answer;
                        }
                    } catch (e) {
                        answerText = q.answer || '';
                    }
                    return {
                        question: q.question || '',
                        answer: answerText
                    };
                });
            }
            return [];
        } catch (e) {
            return [];
        }
    };

    /**
     * Convert Q&A array to FAQ JSON for backend
     */
    convertQAToFaqJson = (faqItems) => {
        const questions = faqItems.map(item => ({
            question: item.question,
            answer: JSON.stringify({
                blocks: [{
                    key: Math.random().toString(36).substring(7),
                    text: item.answer,
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {}
                }],
                entityMap: {}
            })
        }));
        return JSON.stringify({ questions });
    };

    // Greeting handlers
    handleEditGreeting = () => {
        this.setState({
            editingGreeting: true,
            tempGreeting: this.state.greeting,
            greetingError: ''
        });
    };

    validateSpecialCharacters = (text, fieldName) => {
        // Allow normal punctuation and %, but no parentheses or brackets
        const invalidChars = /[()[\]{}]/;
        if (invalidChars.test(text)) {
            return `${fieldName} cannot contain parentheses or brackets`;
        }
        return '';
    };

    handleSaveGreeting = () => {
        const { account } = this.props;
        
        // Validate greeting is not empty
        if (!this.state.tempGreeting || this.state.tempGreeting.trim() === '') {
            this.setState({ greetingError: 'Please enter a greeting message' });
            return;
        }

        // Validate special characters
        const validationError = this.validateSpecialCharacters(this.state.tempGreeting, 'Greeting');
        if (validationError) {
            this.setState({ greetingError: validationError });
            return;
        }
        
        if (account?.accountId) {
            this.props.updateGreeting(account.accountId, this.state.tempGreeting);
        }
        this.setState({
            editingGreeting: false,
            greeting: this.state.tempGreeting,
            greetingError: ''
        });
    };

    handleCancelGreeting = () => {
        this.setState({
            editingGreeting: false,
            tempGreeting: this.state.greeting
        });
    };

    // Knowledge Base handlers
    handleEditKnowledgeBase = () => {
        this.setState({
            editingKb: true,
            tempKnowledgeBase: this.state.knowledgeBase,
            knowledgeBaseError: ''
        });
    };

    handleSaveKnowledgeBase = () => {
        const { account } = this.props;

        // Validate knowledge base is not empty
        if (!this.state.tempKnowledgeBase || this.state.tempKnowledgeBase.trim() === '') {
            this.setState({ knowledgeBaseError: 'Please enter knowledge base content' });
            return;
        }

        // Validate special characters
        const validationError = this.validateSpecialCharacters(this.state.tempKnowledgeBase, 'Knowledge Base');
        if (validationError) {
            this.setState({ knowledgeBaseError: validationError });
            return;
        }

        if (account?.accountId) {
            this.props.updateKnowledgeBase(account.accountId, this.state.tempKnowledgeBase);
        }
        this.setState({
            editingKb: false,
            knowledgeBase: this.state.tempKnowledgeBase,
            knowledgeBaseError: ''
        });
    };

    handleCancelKnowledgeBase = () => {
        this.setState({
            editingKb: false,
            tempKnowledgeBase: this.state.knowledgeBase
        });
    };

    // FAQ handlers
    handleEditFaq = () => {
        this.setState({
            editingFaq: true,
            tempFaqItems: [...this.state.faqItems],
            faqErrors: {}
        });
    };

    handleSaveFaq = () => {
        const { account } = this.props;
        
        // Validate FAQ items - no blank questions or answers
        const errors = {};
        let hasErrors = false;
        
        this.state.tempFaqItems.forEach((item, index) => {
            if (!item.question || item.question.trim() === '') {
                errors[`question_${index}`] = 'Question cannot be empty';
                hasErrors = true;
            } else {
                // Validate special characters in question
                const questionError = this.validateSpecialCharacters(item.question, 'Question');
                if (questionError) {
                    errors[`question_${index}`] = questionError;
                    hasErrors = true;
                }
            }
            
            if (!item.answer || item.answer.trim() === '') {
                errors[`answer_${index}`] = 'Answer cannot be empty';
                hasErrors = true;
            } else {
                // Validate special characters in answer
                const answerError = this.validateSpecialCharacters(item.answer, 'Answer');
                if (answerError) {
                    errors[`answer_${index}`] = answerError;
                    hasErrors = true;
                }
            }
        });

        if (hasErrors) {
            this.setState({ faqErrors: errors });
            return;
        }

        if (account?.accountId) {
            const faqJson = this.convertQAToFaqJson(this.state.tempFaqItems);
            this.props.updateFaq(account.accountId, faqJson);
        }
        this.setState({
            editingFaq: false,
            faqItems: [...this.state.tempFaqItems],
            faqErrors: {}
        });
    };

    handleCancelFaq = () => {
        this.setState({
            editingFaq: false,
            tempFaqItems: [...this.state.faqItems]
        });
    };

    handleCsvUploadFaq = (items) => {
        this.setState({ tempFaqItems: items, faqErrors: {} });
    };

    handleAddFaqItem = () => {
        this.setState({
            tempFaqItems: [...this.state.tempFaqItems, { question: '', answer: '' }]
        });
    };

    handleRemoveFaqItem = (index) => {
        const newItems = [...this.state.tempFaqItems];
        newItems.splice(index, 1);
        this.setState({ tempFaqItems: newItems });
    };

    handleFaqQuestionChange = (index, value) => {
        const newItems = [...this.state.tempFaqItems];
        newItems[index].question = value;
        // Clear error for this question when typing
        const newErrors = { ...this.state.faqErrors };
        delete newErrors[`question_${index}`];
        this.setState({ tempFaqItems: newItems, faqErrors: newErrors });
    };

    handleFaqAnswerChange = (index, value) => {
        const newItems = [...this.state.tempFaqItems];
        newItems[index].answer = value;
        // Clear error for this answer when typing
        const newErrors = { ...this.state.faqErrors };
        delete newErrors[`answer_${index}`];
        this.setState({ tempFaqItems: newItems, faqErrors: newErrors });
    };

    // Bot Delay handler
    handleSaveBotDelay = (botDelay) => {
        const { account } = this.props;
        if (account?.accountId) {
            this.props.updateBotDelay(account.accountId, botDelay);
        }
        this.setState({ botDelay });
    };

    handleEditForm = () => {
        this.setState({ editingForm: true });
    };

    handleCancelEditForm = () => {
        this.setState({ editingForm: false });
        // Reload form to discard changes
        if (this.props.account?.accountId) {
            this.props.loadCallForm(this.props.account.accountId);
        }
    };

    handleTabChange = (event, newValue) => {
        this.setState({ activeTab: newValue });
    };

    handleSaveAIAction = (aiAction, isUpdate) => {
        if (isUpdate) {
            this.props.updateAIActionAsync(aiAction);
        } else {
            this.props.createAIActionAsync(aiAction);
        }
    };

    render() {
        const { classes, user } = this.props;

        const hasGrasshopperGlobalRole = user?.userAccountRoles?.some(
            role => role.userRoleKey === 'GRASSHOPPERGLOBAL'
        );

        // If user doesn't have GRASSHOPPERGLOBAL role, show access denied
        if (!hasGrasshopperGlobalRole) {
            return (
                <MuiCore.Box className={classes.container}>
                    <MuiCore.Paper 
                        style={{ 
                            padding: 40, 
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderRadius: 20,
                            boxShadow: '0 4px 12px rgba(127, 32, 98, 0.08)',
                            border: '1px solid rgba(127, 32, 98, 0.1)'
                        }}
                    >
                        <MuiCore.Typography 
                            variant="h5" 
                            style={{ 
                                color: '#7F2062', 
                                fontFamily: 'Sofia-bold',
                                marginBottom: 16
                            }}
                        >
                            Access Denied
                        </MuiCore.Typography>
                        <MuiCore.Typography 
                            variant="body1"
                            style={{ 
                                color: '#666', 
                                fontFamily: 'Sofia'
                            }}
                        >
                            You do not have permission to access Call Handling.
                            <br />
                            This feature requires the GRASSHOPPER GLOBAL role.
                        </MuiCore.Typography>
                    </MuiCore.Paper>
                </MuiCore.Box>
            );
        }

        const hasFeatureEnabledAccount = user?.userAccountRoles?.some(
            role => role.userRoleKey === 'GRASSHOPPERGLOBAL' && role.showCallHandling === 'Y'
        );

        // If user has role but no accounts with feature, show message
        if (!hasFeatureEnabledAccount) {
            return (
                <MuiCore.Box className={classes.container}>
                    <MuiCore.Paper 
                        style={{ 
                            padding: 40, 
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderRadius: 20,
                            boxShadow: '0 4px 12px rgba(127, 32, 98, 0.08)',
                            border: '1px solid rgba(127, 32, 98, 0.1)'
                        }}
                    >
                        <MuiCore.Typography 
                            variant="h5" 
                            style={{ 
                                color: '#7F2062', 
                                fontFamily: 'Sofia-bold',
                                marginBottom: 16
                            }}
                        >
                            No Accounts Available
                        </MuiCore.Typography>
                        <MuiCore.Typography 
                            variant="body1"
                            style={{ 
                                color: '#666', 
                                fontFamily: 'Sofia'
                            }}
                        >
                            You do not have any GRASSHOPPER GLOBAL accounts with Grasshopper AI feature enabled.
                            <br />
                            Please contact your administrator to enable this feature.
                        </MuiCore.Typography>
                    </MuiCore.Paper>
                </MuiCore.Box>
            );
        }

        return (
            <MuiCore.Box className={classes.container}>
                {/* Account Selection Card */}
                <MuiCore.Box className={classes.accountCard}>
                    <AccountSelect filterByFeature="GRASSHOPPER_AI" />
                </MuiCore.Box>

                {/* Tab Navigation */}
                <MuiCore.Paper className={classes.tabPaper}>
                    <MuiCore.Tabs
                        value={this.state.activeTab}
                        onChange={this.handleTabChange}
                        className={classes.tabs}
                        variant="fullWidth"
                    >
                        <MuiCore.Tab
                            label="Manage Grasshopper AI"
                            style={{
                                fontFamily: 'Sofia',
                                fontWeight: this.state.activeTab === 0 ? 'bold' : 'normal',
                                color: this.state.activeTab === 0 ? '#7F2062' : '#666'
                            }}
                        />
                        <MuiCore.Tab
                            label="Manage AI Actions"
                            style={{
                                fontFamily: 'Sofia',
                                fontWeight: this.state.activeTab === 1 ? 'bold' : 'normal',
                                color: this.state.activeTab === 1 ? '#7F2062' : '#666'
                            }}
                        />
                        <MuiCore.Tab
                            label="Manage Phone Number"
                            style={{
                                fontFamily: 'Sofia',
                                fontWeight: this.state.activeTab === 2 ? 'bold' : 'normal',
                                color: this.state.activeTab === 2 ? '#7F2062' : '#666'
                            }}
                        />
                    </MuiCore.Tabs>
                </MuiCore.Paper>

                {/* Tab 0: Manage Grasshopper AI */}
                {this.state.activeTab === 0 && (
                    <MuiCore.Box>
                        {/* Greeting Section */}
                        <GreetingCard
                            greeting={this.state.greeting}
                            editing={this.state.editingGreeting}
                            tempValue={this.state.tempGreeting}
                            onEdit={this.handleEditGreeting}
                            onSave={this.handleSaveGreeting}
                            onCancel={this.handleCancelGreeting}
                            onChange={(value) => this.setState({ tempGreeting: value, greetingError: '' })}
                            error={this.state.greetingError}
                            classes={classes}
                        />

                        {/* Knowledge Base Section */}
                        <KnowledgeBaseCard
                            knowledgeBase={this.state.knowledgeBase}
                            editing={this.state.editingKb}
                            tempValue={this.state.tempKnowledgeBase}
                            onEdit={this.handleEditKnowledgeBase}
                            onSave={this.handleSaveKnowledgeBase}
                            onCancel={this.handleCancelKnowledgeBase}
                            onChange={(value) => this.setState({ tempKnowledgeBase: value, knowledgeBaseError: '' })}
                            error={this.state.knowledgeBaseError}
                            classes={classes}
                        />

                        {/* Call Forms Section */}
                        {this.state.editingForm ? (
                            <FormFieldsFormCard
                                form={this.props.callForm}
                                onSave={this.handleCancelEditForm}
                                onCancel={this.handleCancelEditForm}
                                onRefreshForm={() => this.props.loadCallForm(this.props.account?.accountId)}
                                createFormFieldAsync={this.props.createFormFieldAsync}
                                updateFormFieldAsync={this.props.updateFormFieldAsync}
                                updateManyFormFieldsAsync={this.props.updateManyFormFieldsAsync}
                                deleteFormFieldAsync={this.props.deleteFormFieldAsync}
                                moveFormFieldsAsync={this.props.moveFormFieldsAsync}
                                createFormVersionAsync={this.props.createFormVersionAsync}
                                activateFormVersionAsync={this.props.activateFormVersionAsync}
                                classes={classes}
                            />
                        ) : (
                            <FormFieldsCard
                                form={this.props.callForm}
                                loading={this.props.callFormLoading}
                                onEdit={this.handleEditForm}
                                classes={classes}
                            />
                        )}

                        {/* Bot Delay Section */}
                        <BotDelayCard
                            ref={this.botDelayCardRef}
                            botDelay={this.state.botDelay}
                            phoneNumbers={this.props.phoneNumbers}
                            onSave={this.handleSaveBotDelay}
                            classes={classes}
                        />

                        {/* FAQ Section */}
                        <FaqCard
                            faqItems={this.state.faqItems}
                            editing={this.state.editingFaq}
                            tempItems={this.state.tempFaqItems}
                            onEdit={this.handleEditFaq}
                            onSave={this.handleSaveFaq}
                            onCancel={this.handleCancelFaq}
                            onAddItem={this.handleAddFaqItem}
                            onRemoveItem={this.handleRemoveFaqItem}
                            onQuestionChange={this.handleFaqQuestionChange}
                            onAnswerChange={this.handleFaqAnswerChange}
                            onCsvUpload={this.handleCsvUploadFaq}
                            errors={this.state.faqErrors}
                            classes={classes}
                        />
                        
                    </MuiCore.Box>
                )}

                {/* Tab 1: Manage AI Actions */}
                {this.state.activeTab === 1 && (
                    <MuiCore.Box>
                        <AIActionsCard
                            accountId={this.props.account?.accountId}
                            aiActions={this.props.aiActions || []}
                            loading={this.props.aiActionsLoading}
                            warmTransferEnabled={this.props.warmTransferEnabled}
                            onSave={this.handleSaveAIAction}
                            classes={classes}
                        />
                    </MuiCore.Box>
                )}

                {/* Tab 2: Manage Phone Number */}
                {this.state.activeTab === 2 && (
                    <MuiCore.Box>
                        {/* Phone Numbers Table */}
                        <PhoneNumbersCard
                            phoneNumbers={this.props.phoneNumbers || []}
                            classes={classes}
                        />
                    </MuiCore.Box>
                )}
                
            </MuiCore.Box>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        account: accountSelectors.getAccount(state),
        user: coreSelectors.getUser(state),
        callHandlingData: state.service.callHandlingData,
        phoneNumbers: state.service.phoneNumbers,
        callForm: state.service.callForm,
        callFormLoading: state.service.callFormLoading,
        aiActions: state.service.aiActions,
        aiActionsLoading: state.service.aiActionsLoading,
        warmTransferEnabled: state.service.warmTransferEnabled
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadCallHandling: (accountId) => dispatch(serviceActions.loadCallHandling(accountId)),
        updateGreeting: (accountId, greeting) => dispatch(serviceActions.updateGreeting(accountId, greeting)),
        updateKnowledgeBase: (accountId, knowledgeBase) => dispatch(serviceActions.updateKnowledgeBase(accountId, knowledgeBase)),
        updateFaq: (accountId, faq) => dispatch(serviceActions.updateFaq(accountId, faq)),
        updateBotDelay: (accountId, botDelay) => dispatch(serviceActions.updateBotDelay(accountId, botDelay)),
        loadPhoneNumbers: (accountId) => dispatch(serviceActions.loadPhoneNumbers(accountId)),
        loadCallForm: (accountId) => dispatch(serviceActions.loadCallForm(accountId)),
        createFormFieldAsync: (field) => dispatch(serviceActions.createFormFieldAsync(field)),
        updateFormFieldAsync: (formFieldId, field) => dispatch(serviceActions.updateFormFieldAsync(formFieldId, field)),
        updateManyFormFieldsAsync: (fields) => dispatch(serviceActions.updateManyFormFieldsAsync(fields)),
        deleteFormFieldAsync: (formFieldId) => dispatch(serviceActions.deleteFormFieldAsync(formFieldId)),
        moveFormFieldsAsync: (formFieldId, beforeFormFieldId) => dispatch(serviceActions.moveFormFieldsAsync(formFieldId, beforeFormFieldId)),
        createFormVersionAsync: (sourceFormVersionId) => dispatch(serviceActions.createFormVersionAsync(sourceFormVersionId)),
        activateFormVersionAsync: (formId, formVersionId) => dispatch(serviceActions.activateFormVersionAsync(formId, formVersionId)),
        loadAIActions: (accountId) => dispatch(serviceActions.loadAIActions(accountId)),
        createAIActionAsync: (aiAction) => dispatch(serviceActions.createAIActionAsync(aiAction)),
        updateAIActionAsync: (aiAction) => dispatch(serviceActions.updateAIActionAsync(aiAction))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CallHandling));
